import clsx from 'clsx'
import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, queryAssignedElements, state } from 'lit/decorators.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'

import SystemCheckLine from '../icons/system/system-check-line/system-check-line.component'
import DapDSStack from '../stack/stack.component'
import styles from './option-item.scss?inline'

/**
 * `dap-ds-option-item`
 * @summary An option item is a selectable item in a list of options.
 * @element dap-ds-option-item
 * @title - Option item
 * @group select,combobox
 * *
 * @csspart base - The main option item container.
 * @csspart prefix - The prefix of the option item.
 * @csspart label - The label of the option item.
 * @csspart suffix - The suffix of the option item.
 *
 * @slot prefix - The prefix of the option item.
 * @slot - The label of the option item.
 * @slot suffix - The suffix of the option item.
 *
 * @cssproperty --dds-option-item-padding-vertical - The vertical padding of the option item (default: var(--dds-spacing-100))
 * @cssproperty --dds-option-item-padding-horizontal - The horizontal padding of the option item (default: var(--dds-spacing-200))
 * @cssproperty --dds-option-item-border-radius - The border radius of the option item (default: var(--dds-radius-small, 4px))
 * @cssproperty --dds-option-item-color - The text color of the option item (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-option-item-hover-bg - The background color of the option item when hovered (default: var(--dds-background-neutral-medium))
 * @cssproperty --dds-option-item-active-bg - The background color of the option item when active (default: var(--dds-background-neutral-strong))
 * @cssproperty --dds-option-item-disabled-color - The text color of the option item when disabled (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-option-item-high-contrast-border - The border color of the option item in high contrast mode (default: var(--dds-border-neutral-transparent-interactive, #fff500))
 * @cssproperty --dds-option-item-selected-font-weight - The font weight of the option item when selected (default: var(--dds-font-weight-bold))
 * @cssproperty --dds-option-item-xs-font-size - The font size of the option item in extra small size (default: var(--dds-font-sm))
 * @cssproperty --dds-option-item-lg-font-size - The font size of the option item in large size (default: var(--dds-font-lg))
 * @cssproperty --dds-option-item-selected-icon-width - The width of the selected icon (default: var(--dds-spacing-400))
 */
export default class DapDSOptionItem extends SizedMixin(DdsElement) {
  static tagName = 'dap-ds-option-item'

  static dependencies = {
    'dap-ds-stack': DapDSStack,
    'dap-ds-icon-check-line': SystemCheckLine,
  }

  /** The selected state of the option item */
  @property({ type: Boolean, reflect: true }) selected?: boolean

  /** Wheteher the item is selectable, whether it shows the selected icon */
  @property({ type: String }) selectable? = 'true'

  /** The position of the selected icon */
  @property({ type: String }) selectedIconPosition? = 'suffix'

  /** The value of the option item */
  @property({ reflect: true }) value?: any

  /** The disabled state of the option item */
  @property({ type: Boolean, reflect: true }) disabled?: boolean

  /** The focused state of the option item */
  @property({ type: Boolean }) focused?: boolean

  /** The label of the option item, it can be a simplier version of an item */
  @property() label?: string
  @queryAssignedElements({ slot: 'prefix' })
  private readonly _prefixNode!: NodeListOf<HTMLElement>
  @queryAssignedElements({ slot: 'suffix' })
  private readonly _postfixNode!: NodeListOf<HTMLElement>

  @state() private _hasPrefix = false
  @state() private _hasSuffix = false

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    this._hasPrefix = !!this._prefixNode.length
    this._hasSuffix = !!this._postfixNode.length

    this.addEventListener('click', this.handleClick)
    this.setAttribute('role', 'option')
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('disabled')) {
      this.setAttribute('tabindex', this.disabled ? '-1' : '0')
      this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false')
    }

    if (_changedProperties.has('selected')) {
      this.setAttribute('aria-selected', this.selected ? 'true' : 'false')
    }
  }

  private handleClick(event: MouseEvent): void {
    if (this.disabled) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  public get optionText(): string {
    return this.label ?? this.textContent?.trim() ?? ''
  }

  render() {
    return html`
      <div
        part="base"
        class=${clsx('option-item', `option-item--${this.size}`, {
          'option-item--selected': this.selected,
          'option-item--disabled': this.disabled,
          'option-item--focused': this.focused,
        })}>
        <dap-ds-stack direction="row" class="option-item__stack">
          ${this.selectable === 'true' && this.selectedIconPosition === 'prefix'
            ? html`<div class=${'selected-icon'}>
                ${this.selected
                  ? html`<dap-ds-icon-check-line
                      size="16"></dap-ds-icon-check-line>`
                  : nothing}
              </div>`
            : nothing}
          <div
            part="prefix"
            class=${clsx('option-item__prefix', {
              'option-item__prefix--visible': !!this._hasPrefix,
            })}>
            <slot name="prefix"></slot>
          </div>
          <div part="label" class="label">
            <slot></slot>
          </div>
          <div
            part="suffix"
            class=${clsx('option-item__suffix', {
              'option-item__suffix--visible': !!this._hasSuffix,
            })}>
            <slot name="suffix"></slot>
          </div>
        </dap-ds-stack>
        ${this.selectable === 'true' && this.selectedIconPosition === 'suffix'
          ? html`<div class=${'selected-icon-suffix'}>
              ${this.selected
                ? html`<dap-ds-icon-check-line
                    size="16"></dap-ds-icon-check-line>`
                : nothing}
            </div>`
          : nothing}
        <span
          part="high-contrast"
          class=${clsx('option-item__high-contrast', {
            'option-item__high-contrast--disabled': this.disabled,
          })}></span>
      </div>
    `
  }
}
